import {
  getDefaultShipper,
  getDefaultPickupDateTimeInfo,
  getDefaultPickupDateTimeErrorInfo,
  getDefaultShipperError,
  getLatestReadyCloseTime,
} from "./ShipperInfo/utils";
import {
  getDefaultShipment,
  getDefaultShipmentError,
} from "./ShipmentInfo/utils";
import {
  getDefaultEditReasonInfo,
  getDefaultEditReasonErrorInfo,
} from "./EditReasonInfo/utils";
import { getDefaultBillingInfo } from "./Billing/utils";
import {
  getDefaultRequestorInfo,
  getDefaultRequestorErrorInfo,
} from "./ReservationRequestorInfo/utils";
import { PICKUP_MANAGEMENT_MODES, PICKUP_MANAGEMENT_BILLING_MODES, WARNING_DETAILS } from "./constants";
import { convertDateTimeToTimeString } from '@dataGrid/gridUtilities';
import { format } from "date-fns";
import { getDataWithQueryString, getDataWithDeduplication } from "../../../../../../api/componentAPIs";
import Label from "./MergePickupShipperDetails/RadioLabel"
import { getRadioLabelData } from "./MergePickupShipperDetails/utils";
import { SVG_ICON_NAME } from "../../../../../../appConstants";
const getDefaultPickupManagementInfo = (pickup, terminalOffset) => {
  return {
    shipperInfo: getDefaultShipper(pickup),
    shipmentInfo: pickup
      ? pickup.shipments.map((shipment) => getDefaultShipment(shipment))
      : [],
    pickupDateTimeInfo: getDefaultPickupDateTimeInfo(pickup, terminalOffset),
    specialInstructions: pickup?.specialInstructions ?? "",
    billingInfo: getDefaultBillingInfo(pickup),
    requestorInfo: getDefaultRequestorInfo(pickup),
    editReasonInfo: getDefaultEditReasonInfo(pickup),
    isSwapPickup: pickup?.isSwapPickup ?? false,
    isDockDropPickup: pickup?.isDockDropPickup ?? false,
    isExhibitPickup: pickup?.isExhibitPickup ?? false,
    pickupId: pickup?.pickupId || '',
    isLiftGate : pickup?.isLiftGate ?? false,
  };
};

const getDefaultPickupManagementErrorInfo = () => {
  return {
    pickupDateTimeInfo: getDefaultPickupDateTimeErrorInfo(),
    shipperInfo: getDefaultShipperError(),
    shipmentInfo: [getDefaultShipmentError()],
    requestorInfo: getDefaultRequestorErrorInfo(),
    editReasonInfo: getDefaultEditReasonErrorInfo(),
    swapAndDrop: "",
  };
};

const getSavePickupMessage = (mode) => {
  switch (mode) {
    case PICKUP_MANAGEMENT_MODES.CREATE_PICKUP:
      return {
        successMsg: "Pickup Created Successfully",
        errorMsg: "Create Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.EDIT_PICKUP:
      return {
        successMsg: "Pickup Updated Successfully",
        errorMsg: "Edit Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.MERGE_PICKUP:
      return {
        successMsg: "Pickups Merged Successfully",
        errorMsg: "Merge Pickups Failed",
      };
    case PICKUP_MANAGEMENT_MODES.NO_FREIGHT_PICKUP:
      return {
        successMsg: "No Freight Pickup Successful",
        errorMsg: "No Freight Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.BULK_EDIT_PICKUP:
      return {
        successMsg: "Group Pickups Updated Successfully",
        errorMsg: "Bulk Edit Pickups Failed",
      };
    case PICKUP_MANAGEMENT_MODES.BULK_VOID_PICKUP:
      return {
        successMsg: "Group Pickups Voided Successfully",
        errorMsg: "Void Group Failed",
      };
    default:
      return {
        successMsg: "Void Pickup Successful",
        errorMsg: "Void Pickup Failed",
      };
  }
};

const isReadOnlyPickupViewMode = (mode) => {
  return [
    PICKUP_MANAGEMENT_MODES.VOID_PICKUP,
    PICKUP_MANAGEMENT_MODES.UNVOID_PICKUP,
    PICKUP_MANAGEMENT_MODES.NO_FREIGHT_PICKUP,
    PICKUP_MANAGEMENT_MODES.BULK_VOID_PICKUP,
  ].includes(mode);
};

const isMergePickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.MERGE_PICKUP === mode;
}

const isBulkEditPickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.BULK_EDIT_PICKUP === mode;
};

const isBulkVoidPickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.BULK_VOID_PICKUP === mode;
};

const isBulkPickupMode = (mode) => {
  return isBulkEditPickupMode(mode) || isBulkVoidPickupMode(mode);
};

const isPickupManagementBillingMode = (mode) =>
  PICKUP_MANAGEMENT_BILLING_MODES.includes(mode);

const shouldShowRequestorInPickupManagement = ({ enableRequestorInfo, mode }) => {
  return (
    Boolean(enableRequestorInfo) &&
    isPickupManagementBillingMode(mode) &&
    !isBulkEditPickupMode(mode) &&
    !isBulkVoidPickupMode(mode)
  );
};

const isCreatePickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.CREATE_PICKUP === mode;
}

const isCreateOrEditPickupMode = (mode) => {
  return [
    PICKUP_MANAGEMENT_MODES.CREATE_PICKUP,
    PICKUP_MANAGEMENT_MODES.EDIT_PICKUP
  ].includes(mode);
}

/** Embargo validation applies to Create, Edit, Bulk Edit, and Merge (not Process Pickup). */
const isEmbargoPickupMode = (mode) => {
  return [
    PICKUP_MANAGEMENT_MODES.CREATE_PICKUP,
    PICKUP_MANAGEMENT_MODES.EDIT_PICKUP,
    PICKUP_MANAGEMENT_MODES.BULK_EDIT_PICKUP,
    PICKUP_MANAGEMENT_MODES.MERGE_PICKUP
  ].includes(mode);
};

const isEditOrMergePickupMode = (mode) => {
  return [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP, PICKUP_MANAGEMENT_MODES.MERGE_PICKUP].includes(mode);
};

const getConfirmationButtonLabel = (mode,driver) => {
  switch (mode) {
    case PICKUP_MANAGEMENT_MODES.CREATE_PICKUP:
      return "Create Pickup";
    case PICKUP_MANAGEMENT_MODES.EDIT_PICKUP:
    case PICKUP_MANAGEMENT_MODES.BULK_EDIT_PICKUP:
      return "Done";
    case PICKUP_MANAGEMENT_MODES.MERGE_PICKUP:
      return driver ? "Merge Pickups & Assign Driver":"Merge Pickups";
    case PICKUP_MANAGEMENT_MODES.NO_FREIGHT_PICKUP:
      return "Mark as No Freight"
    case PICKUP_MANAGEMENT_MODES.VOID_PICKUP:
      return "Void Pickup";
    case PICKUP_MANAGEMENT_MODES.BULK_VOID_PICKUP:
      return "Void Group Pickups";
    default:
      return "Unvoid Pickup";
  }
};

const getBaselinePickupForDateTimeCheck = (pickupData) => {
  if (Array.isArray(pickupData) && pickupData.length > 0) {
    const selectedPickup =
      pickupData.find((pickup) => pickup.driverId) ?? pickupData[0];
    return {
      ...selectedPickup,
      ...getLatestReadyCloseTime(pickupData),
    };
  }
  return pickupData ?? null;
};

const checkIsDateTimeUpdated = (pickupData, pickupDateTimeInfo) => {
  const baseline = getBaselinePickupForDateTimeCheck(pickupData);
  if (!baseline) {
    return false;
  }
  const { pickupReadyTime, pickupCloseTime, pickupDate } = baseline;
  const { readyTime, closeTime } = pickupDateTimeInfo;
  return (
    convertDateTimeToTimeString(readyTime) !== pickupReadyTime ||
    convertDateTimeToTimeString(closeTime) !== pickupCloseTime ||
    pickupDate !== format(pickupDateTimeInfo.pickupDate, "yyyy-MM-dd")
  );
};

const getSelectedTerminalDetails = (selectedTerminal, terminals) => {
  return terminals.find((terminal) => terminal.value === selectedTerminal);
};

const resolveSelectedTerminal = ({ preferredTerminalCode, userTerminal, terminals }) => {
  return (
    getSelectedTerminalDetails(preferredTerminalCode, terminals) ||
    getSelectedTerminalDetails(userTerminal.code, terminals) || {
      label: userTerminal.code,
      value: userTerminal.code,
      offset: userTerminal.offset,
    }
  );
};

const getWarningLabel = (mode) => {
  const {WARNING_LABELS: {CREATE, EDIT, MERGE}} = WARNING_DETAILS;
  const warningLabel = {
    [PICKUP_MANAGEMENT_MODES.CREATE_PICKUP]: CREATE,
    [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP]: EDIT,
    [PICKUP_MANAGEMENT_MODES.MERGE_PICKUP]: MERGE,
  }
  return warningLabel[mode]
}

const getFilteredShipmentsData = (shipmentInfo) => {
  return shipmentInfo.reduce((acc, shipment) => {
    const {
      pickupShipmentRowId,
      isZipUpdatedManually,
      isFreezable,
      ...shipmentData
    } = shipment;
    if (Object.values(shipmentData).filter(Boolean).length === 0) {
      return acc;
    }
    return [
      ...acc,
      {
        ...shipmentData,
        handlingUnits: shipmentData.handlingUnits || 0,
        pieces: shipmentData.pieces || 0,
        weight: shipmentData.weight || 0,
      },
    ];
  }, []);
};

const getFormattedShippers = (pickupData, selectedPossibleMergePickupId) => {
  const selectedPickupId = selectedPossibleMergePickupId?.toLowerCase();
  let newPickupData = pickupData;
  if (selectedPickupId) {
    /* if possible merge pickup icon click on grid select that shipper in 1st position */
    newPickupData = moveSelectedPossibleMergePickupToFirstPosition(
      pickupData,
      selectedPickupId
    );
  }

  return newPickupData.map((pickup) => {
    return {
      label: <Label data={getRadioLabelData(pickup)} pickupId={pickup.pickupId} />,
      value: JSON.stringify({
        rmaxShipperCode: pickup.rmaxShipperCode,
        pickupId: pickup.pickupId
      }),
      pickupId: pickup.pickupId
    };
  });
};

const getFormattedTerminals = (terminals) => {
  return terminals.map((terminal) => ({
    label: terminal.terminalCode,
    value: terminal.terminalCode,
    offset: terminal.utcToLocalOffset,
  }));
}

const fetchTerminalAndRestrictionsData = (terminalCode, pickupDate) => {
  return Promise.all([
    getDataWithQueryString(
      "GetAllAppTerminals",
      `terminalCode=${terminalCode}`
    ),
    getDataWithQueryString(
      "PickupRestrictions",
      pickupDate
        ? `terminalCode=${terminalCode}&currentDate=${pickupDate}`
        : `terminalCode=${terminalCode}`
    ),
  ]);
};

const getCombinedSpecialInstructions = (specialInstructions) => {
  return specialInstructions.join(" ").trim();
}

const fetchLocationMappingData = ({
  terminalCode,
  zipCode,
}) => {
  return getDataWithDeduplication(
    "GetLocationMapping",
    `terminalCode=${terminalCode}&zip=${zipCode}`,
  );
};

const getFirstSelectedAssignedDriverPickup = (pickupData) => {
  return pickupData.find(pickup => pickup.driverId);
}

const moveSelectedPossibleMergePickupToFirstPosition = (
  pickupData,
  selectedPickupId
) => {
  const selectedPickupIndex = pickupData.findIndex(
    (pickup) => pickup.pickupId.toLowerCase() === selectedPickupId
  );
  const newPickupData = [...pickupData];
  if (selectedPickupIndex !== -1) {
    const selectedPickup = newPickupData.splice(selectedPickupIndex, 1);
    return [...selectedPickup, ...newPickupData];
  }
  return pickupData;
};

const getShipperByPickupId = (shipperList, pickupId) => {
  if (!pickupId) {
    return null;
  }
  return (
    shipperList.find(
      (shipper) => shipper.pickupId.toLowerCase() === pickupId.toLowerCase()
    )?.value ?? null
  );
};

const validateToggle = ({ isDockDropPickup, isSwapPickup }) => {
  return isSwapPickup && isDockDropPickup
    ? "Pickup can't be swap and dock drop enabled at the same time"
    : null;
};

const pickupTypeValueGetter = (params) => {
  const { data } = params;
  let pickupTypes = data.priority ? [...data.priority] : [];
  if (data.is3PlPickup) {
    pickupTypes.push(SVG_ICON_NAME._3PL);
  }
  if (data.isSwapPickup) {
    pickupTypes.push(SVG_ICON_NAME.SWAP);
  }
  if (data.isDockDropPickup) {
    pickupTypes.push(SVG_ICON_NAME.DockDrop);
  }
  if (data.isExhibitPickup) {
    pickupTypes.push(SVG_ICON_NAME.ExhibitPickup);
  }
  if (data.isDailyPickup) {
    pickupTypes.push(SVG_ICON_NAME.dailyPickup);
  }
  return pickupTypes.filter(Boolean);
};

const getReasonCodeOptions = (reasonCodeList) => {
  return reasonCodeList.map((reasonCode) => ({
    labelText: reasonCode.label,
    value: String(reasonCode.value),
  }));
}

const getBulkGroupId = (pickupData) => {
  if (Array.isArray(pickupData) && pickupData.length > 0) {
    return pickupData[0].groupId ?? null;
  }
  if (pickupData && typeof pickupData === "object" && pickupData.groupId) {
    return pickupData.groupId;
  }
  return null;
};

const getBulkPickupNumbers = (pickupData) => {
  if (!Array.isArray(pickupData)) {
    return [];
  }
  const numbers = pickupData.reduce((acc, pickup) => {
    if (pickup?.pickupNumber) {
      acc.push(pickup.pickupNumber);
    }
    return acc;
  }, []);
  return [...new Set(numbers)];
};

const getBulkCombinedShipments = (pickupData) => {
  if (!Array.isArray(pickupData)) {
    return [];
  }
  return pickupData.reduce((acc, pickup) => {
    const pickupShipments = Array.isArray(pickup?.shipments) ? pickup.shipments : [];
    pickupShipments.forEach((shipment) => {
      acc.push({
        ...shipment,
        pickupId: pickup.pickupId,
      });
    });
    return acc;
  }, []);
};

// Grouped grid rows may mark `isPrimary`; align with GroupPickup grouping (primary or first).
const getPrimaryOrFirstPickupInGroup = (pickupData) => {
  if (!Array.isArray(pickupData) || pickupData.length === 0) {
    return null;
  }
  return pickupData.find((p) => p?.isPrimary) ?? pickupData[0];
};

const getBulkPickupRowForPickupInfo = (pickupData) =>
  getPrimaryOrFirstPickupInGroup(pickupData);

/**
 * For grouped pickups (bulk edit / bulk void), each pickup-type toggle is true if **any**
 * member pickup has it set (logical OR). Single-pickup arrays behave like that pickup alone.
 */
const mergeBulkGroupPickupTypeToggles = (pickupData, pickupLikeObject) => {
  if (!Array.isArray(pickupData) || pickupData.length === 0) {
    return pickupLikeObject;
  }
  return {
    ...pickupLikeObject,
    isSwapPickup: pickupData.some((p) => Boolean(p?.isSwapPickup)),
    isDockDropPickup: pickupData.some((p) => Boolean(p?.isDockDropPickup)),
    isExhibitPickup: pickupData.some((p) => Boolean(p?.isExhibitPickup)),
  };
};

const getBulkEditInitialData = (pickupData) => {
  if (!Array.isArray(pickupData) || pickupData.length === 0) {
    return null;
  }
  const basePickup = getPrimaryOrFirstPickupInGroup(pickupData);
  const combinedShipments = getBulkCombinedShipments(pickupData).map((shipment) => ({
    ...shipment,
  }));
  return mergeBulkGroupPickupTypeToggles(pickupData, {
    ...basePickup,
    ...getLatestReadyCloseTime(pickupData),
    shipments: combinedShipments,
    isLiftGate: pickupData.some((pickup) => pickup?.isLiftGate),
  });
};

const getRemovedShipmentsFromSnapshot = (originalShipments, currentShipments) => {
  if (!Array.isArray(originalShipments) || originalShipments.length === 0) {
    return [];
  }
  const currentIds = new Set(
    (currentShipments || [])
      .map((shipment) => shipment?.pickupShipmentId)
      .filter(Boolean)
  );
  return originalShipments
    .filter((shipment) => shipment?.pickupShipmentId && !currentIds.has(shipment.pickupShipmentId))
    .map((shipment) => ({
      pickupId: shipment.pickupId,
      pickupShipmentId: shipment.pickupShipmentId,
      weight: shipment.weight,
      packageCode: shipment.packageCode ?? null,
    }));
};

export {
  getDefaultPickupManagementInfo,
  getDefaultPickupManagementErrorInfo,
  getSavePickupMessage,
  checkIsDateTimeUpdated,
  isReadOnlyPickupViewMode,
  isMergePickupMode,
  getConfirmationButtonLabel,
  getSelectedTerminalDetails,
  resolveSelectedTerminal,
  getWarningLabel,
  getFilteredShipmentsData,
  getFormattedShippers,
  getFormattedTerminals,
  fetchTerminalAndRestrictionsData,
  isCreatePickupMode,
  isCreateOrEditPickupMode,
  isEmbargoPickupMode,
  getCombinedSpecialInstructions,
  fetchLocationMappingData,
  getFirstSelectedAssignedDriverPickup,
  getShipperByPickupId,
  validateToggle,
  isEditOrMergePickupMode,
  pickupTypeValueGetter,
  getReasonCodeOptions,
  isBulkEditPickupMode,
  isBulkVoidPickupMode,
  isBulkPickupMode,
  getBulkGroupId,
  getBulkPickupNumbers,
  getBulkCombinedShipments,
  getBulkEditInitialData,
  mergeBulkGroupPickupTypeToggles,
  getBulkPickupRowForPickupInfo,
  getRemovedShipmentsFromSnapshot,
  shouldShowRequestorInPickupManagement,
  isPickupManagementBillingMode,
};
